import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import manualPaymentVerificationService from '../../../services/manualPaymentVerificationService';

const SUBSCRIPTION_TIERS = [
  { value: 'basic', label: 'Basic (K299/month)', price: 299 },
  { value: 'standard', label: 'Standard (K599/month)', price: 599 },
  { value: 'premium', label: 'Premium (K999/month)', price: 999 },
  { value: 'enterprise', label: 'Enterprise (K1499/month)', price: 1499 }
];

const AccountActivationWorkflow = ({ onActivationComplete }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activatingId, setActivatingId] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activationForm, setActivationForm] = useState({
    subscriptionTier: 'basic',
    employeeCount: 1,
    notes: ''
  });
  const [bulkSelection, setBulkSelection] = useState([]);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await manualPaymentVerificationService?.getPendingActivationRequests();
      setAccounts(data);
    } catch (err) {
      setError(err?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateAccount = async () => {
    if (!selectedAccount) return;

    try {
      setActivatingId(selectedAccount?.companyId);
      setError('');

      await manualPaymentVerificationService?.activateClientAccount({
        companyId: selectedAccount?.companyId,
        subscriptionTier: activationForm?.subscriptionTier,
        employeeCount: parseInt(activationForm?.employeeCount) || 1,
        notes: activationForm?.notes
      });

      // Reset form
      setSelectedAccount(null);
      setActivationForm({ subscriptionTier: 'basic', employeeCount: 1, notes: '' });

      // Reload data
      await loadAccounts();
      onActivationComplete?.();

      alert('Account activated successfully!');
    } catch (err) {
      setError(err?.message || 'Activation failed');
    } finally {
      setActivatingId(null);
    }
  };

  const handleBulkActivation = async () => {
    if (bulkSelection?.length === 0) return;

    try {
      setLoading(true);
      setError('');

      // Activate all selected accounts
      for (const accountId of bulkSelection) {
        const account = accounts?.find(a => a?.id === accountId);
        if (account) {
          await manualPaymentVerificationService?.activateClientAccount({
            companyId: account?.companyId,
            subscriptionTier: 'basic',
            employeeCount: account?.activeEmployeeCount || 1,
            notes: 'Bulk activation'
          });
        }
      }

      setBulkSelection([]);
      await loadAccounts();
      onActivationComplete?.();

      alert(`${bulkSelection?.length} accounts activated successfully!`);
    } catch (err) {
      setError(err?.message || 'Bulk activation failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkSelection = (accountId) => {
    setBulkSelection(prev =>
      prev?.includes(accountId)
        ? prev?.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      suspended: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading && accounts?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bulk Actions */}
      {bulkSelection?.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="CheckSquare" className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-foreground">
              {bulkSelection?.length} account{bulkSelection?.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleBulkActivation}>
              <Icon name="Zap" size={14} className="mr-1" />
              Bulk Activate
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkSelection([])}>
              Clear
            </Button>
          </div>
        </div>
      )}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}
      {/* Accounts Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={bulkSelection?.length === accounts?.length && accounts?.length > 0}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setBulkSelection(accounts?.map(a => a?.id));
                      } else {
                        setBulkSelection([]);
                      }
                    }}
                    className="rounded border-border"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Current Tier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Employees</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {accounts?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No pending activation requests
                  </td>
                </tr>
              ) : (
                accounts?.map((account) => (
                  <tr key={account?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={bulkSelection?.includes(account?.id)}
                        onChange={() => toggleBulkSelection(account?.id)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{account?.companyName}</p>
                        <p className="text-xs text-muted-foreground">{account?.contactEmail}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{account?.currentTier?.toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{account?.activeEmployeeCount}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusBadgeColor(account?.subscriptionStatus)}`}>
                        {account?.subscriptionStatus?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        onClick={() => setSelectedAccount(account)}
                        disabled={activatingId === account?.companyId}
                      >
                        {activatingId === account?.companyId ? (
                          <Icon name="Loader2" size={14} className="animate-spin" />
                        ) : (
                          <>
                            <Icon name="UserCheck" size={14} className="mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Activation Modal */}
      {selectedAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">Activate Client Account</h3>
              <button
                onClick={() => setSelectedAccount(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Account Details */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Company:</span>
                  <span className="text-sm font-semibold text-foreground">{selectedAccount?.companyName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Legal Name:</span>
                  <span className="text-sm text-foreground">{selectedAccount?.companyLegalName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Contact:</span>
                  <span className="text-sm text-foreground">{selectedAccount?.contactEmail}</span>
                </div>
              </div>

              {/* Activation Form */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Subscription Tier</label>
                  <Select
                    value={activationForm?.subscriptionTier}
                    onChange={(e) => setActivationForm({ ...activationForm, subscriptionTier: e?.target?.value })}
                  >
                    {SUBSCRIPTION_TIERS?.map((tier) => (
                      <option key={tier?.value} value={tier?.value}>
                        {tier?.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Employee Count</label>
                  <Input
                    type="number"
                    min="1"
                    value={activationForm?.employeeCount}
                    onChange={(e) => setActivationForm({ ...activationForm, employeeCount: e?.target?.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Activation Notes</label>
                  <textarea
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    rows="3"
                    placeholder="Add activation notes (optional)"
                    value={activationForm?.notes}
                    onChange={(e) => setActivationForm({ ...activationForm, notes: e?.target?.value })}
                  />
                </div>
              </div>

              {/* Pricing Preview */}
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Monthly Billing:</span>
                  <span className="text-xl font-bold text-primary">
                    K{SUBSCRIPTION_TIERS?.find(t => t?.value === activationForm?.subscriptionTier)?.price || 0}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4">
                <Button
                  onClick={handleActivateAccount}
                  disabled={activatingId !== null}
                  className="flex-1"
                >
                  {activatingId ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Activating...
                    </>
                  ) : (
                    <>
                      <Icon name="UserCheck" size={16} className="mr-2" />
                      Activate Account
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedAccount(null)}
                  disabled={activatingId !== null}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountActivationWorkflow;